import { Link } from "react-router-dom";
import { ApplicationStateRegion, ApplicationSurface } from "../../../components/application";
import ProgramEvidenceComponents from "./ProgramEvidenceComponents";
import {
  BYCE_OUTCOME_WEIGHTS,
  componentValue,
  completedSessionDateItems,
  currentStageLabel,
  developmentResultItems,
  developmentResultSummary,
  finalRatingValue,
  finalRatingExecutionValue,
  participantStatusValue,
  reflectionValue,
  stageItems,
} from "./byceEvidencePresentation";

const EvidenceFact = ({ label, value, supporting = false }) => (
  <div className={supporting ? "av-byce-evidence-fact av-byce-evidence-fact--supporting" : "av-byce-evidence-fact"}>
    <dt>{label}</dt><dd>{value}</dd>
  </div>
);

const programProgressFor = (participant, programId) =>
  participant.programs?.find(program => program.programId === programId) || null;

export function ByceFacilitatorOverview({ program, participants = [], projectEvidence, projectSlug }) {
  const rows = participants.map(participant => ({
    participant,
    progress: programProgressFor(participant, program.id),
    evidence: participant.evidenceByProgram?.[program.id] || null,
  }));
  const stageCounts = rows.reduce((counts, row) => {
    const label = currentStageLabel(row.progress);
    counts.set(label, (counts.get(label) || 0) + 1);
    return counts;
  }, new Map());

  return <>
    <p className="av-project-facilitator-overview__metadata">{program.title} · Program version {program.version}</p>
    <div className="av-byce-cohort-summary">
      <ApplicationSurface variant="nested">
        <p className="av-project-facilitator-overview__metric">{participants.length}</p>
        <p>{participants.length === 1 ? "Participant" : "Participants"}</p>
      </ApplicationSurface>
      <ApplicationSurface variant="nested">
        <h3>Current stage distribution</h3>
        {stageCounts.size ? <dl className="av-byce-stage-distribution">
          {[...stageCounts].map(([label, count]) => <div key={label}><dt>{label}</dt><dd>{count}</dd></div>)}
        </dl> : <p>No participant progress is available yet.</p>}
      </ApplicationSurface>
    </div>

    <h3>Participant progress</h3>
    {rows.length ? <div className="av-byce-progress-table-wrap"><table className="av-byce-progress-table">
      <thead><tr><th>Participant</th><th>Current stage</th><th>Practice</th><th>Spacing</th><th>Consistency</th><th>Reflection</th><th>Final rating</th><th>Grade</th></tr></thead>
      <tbody>{rows.map(({ participant, progress, evidence }) => <tr key={participant.membershipId}>
        <th scope="row"><Link to={`/app/facilitate/project/${projectSlug}/member/${participant.membershipId}`}>{participant.displayIdentity}</Link></th>
        <td>{currentStageLabel(progress)}</td>
        <td>{componentValue(evidence, "completed-sessions")}</td>
        <td>{participantStatusValue(evidence, "spacing")}</td>
        <td>{participantStatusValue(evidence, "checkpoints")}</td>
        <td>{reflectionValue(evidence)}</td>
        <td>{finalRatingValue(evidence, progress)}</td>
        <td>— / 30</td>
      </tr>)}</tbody>
    </table></div> : <ApplicationStateRegion status="empty" message="No Project participants are available in the authorized view." />}

    <details className="av-byce-readiness">
      <summary>Program evidence readiness</summary>
      <ProgramEvidenceComponents evidence={projectEvidence} heading={program.title} />
    </details>
  </>;
}

const Section = ({ title, children }) => <section className="av-byce-detail-section"><h4>{title}</h4><dl>{children}</dl></section>;

export function ByceParticipantEvidence({ evidence, progress }) {
  if (!evidence) return null;
  const configuredStages = stageItems(evidence);
  const completedSessionDates = completedSessionDateItems(evidence);
  const developmentItems = developmentResultItems(evidence);
  const finalItems = developmentItems.filter(item => item.label.startsWith("stage-4-independent"));
  const practiceItems = developmentItems.filter(item => !item.label.startsWith("stage-4-independent"));
  return <div className="av-byce-participant-evidence">
    <Section title="Current position">
      <EvidenceFact label="Current stage" value={currentStageLabel(progress)} />
      <EvidenceFact label="Recent activity" value={progress?.currentAttempt?.lastActivityAt ? new Date(progress.currentAttempt.lastActivityAt).toLocaleDateString() : "Not started"} />
      {configuredStages.length ? <EvidenceFact label="Configured collaborator-review stages" value={configuredStages.map(item => `${item.label}: ${item.value}`).join(" · ")} /> : null}
    </Section>
    <Section title="Practice">
      <EvidenceFact label="Sessions completed" value={componentValue(evidence, "completed-sessions")} />
      {completedSessionDates.map(item => <EvidenceFact key={`${item.label}-${item.value}`} label={item.label} value={new Date(item.value).toLocaleDateString()} />)}
      <EvidenceFact label="Distinct recorded days" value="Not available yet" supporting />
      <EvidenceFact label="Grading structure" value="— / 8" />
    </Section>
    <Section title="Spacing">
      <EvidenceFact label="Spacing status" value={participantStatusValue(evidence, "spacing")} />
      <EvidenceFact label="Grading structure" value="— / 6" />
    </Section>
    <Section title="Consistency checks">
      <EvidenceFact label="Checkpoint and trend status" value={participantStatusValue(evidence, "checkpoints")} />
      <EvidenceFact label="Grading structure" value="— / 4" />
    </Section>
    <Section title="Reflection">
      <EvidenceFact label="Submission" value={reflectionValue(evidence)} />
      <EvidenceFact label="Grading structure" value="— / 4" />
    </Section>
    <Section title="Final rating">
      <EvidenceFact label="Execution" value={finalRatingExecutionValue(progress)} />
      <EvidenceFact label="Ratings within expert range" value={finalRatingValue(evidence, progress)} />
      {finalItems.map(item => <EvidenceFact key={item.label} label={item.label} value={item.value} />)}
      {finalItems.length ? <EvidenceFact label="Development summary" value={developmentResultSummary(evidence)} /> : null}
      {finalItems.length ? <EvidenceFact label="Sample status" value="Provisional collaborator-review samples; not the final pilot capstone result" supporting /> : null}
      <EvidenceFact label="Graded capstone result" value="Not available yet" />
      <EvidenceFact label="Grading structure" value="— / 8" />
    </Section>
    {practiceItems.length ? <Section title="Practice results">
      {practiceItems.map(item => <EvidenceFact key={item.label} label={item.label} value={item.value} />)}
      <EvidenceFact label="Development summary" value={developmentResultSummary(evidence)} />
      <EvidenceFact label="Sample status" value="Provisional collaborator-review samples; not final pilot results" supporting />
    </Section> : null}
    <Section title="Grade">
      {BYCE_OUTCOME_WEIGHTS.map(component => <EvidenceFact key={component.key} label={component.label} value={`— / ${component.points}`} />)}
      <EvidenceFact label="Total" value="— / 30" />
    </Section>
    <details className="av-byce-process-evidence">
      <summary>Process evidence</summary>
      <dl>
        <EvidenceFact label="Ratings submitted" value={componentValue(evidence, "rating-evidence")} supporting />
        <EvidenceFact label="Confidence judgments" value={componentValue(evidence, "confidence-evidence")} supporting />
        <EvidenceFact label="Target plays" value={componentValue(evidence, "target-plays")} supporting />
        <EvidenceFact label="Anchor plays" value={componentValue(evidence, "anchor-use")} supporting />
        <EvidenceFact label="Open-to-submit duration" value={componentValue(evidence, "engagement-duration")} supporting />
        <EvidenceFact label="Listening conditions" value={componentValue(evidence, "listening-conditions")} supporting />
        <EvidenceFact label="Review signals" value={componentValue(evidence, "attention-review")} supporting />
      </dl>
    </details>
    <details className="av-byce-readiness">
      <summary>Evidence availability</summary>
      <ProgramEvidenceComponents evidence={evidence} heading="BYCE evidence readiness" />
    </details>
  </div>;
}

export const byceMemberColumns = ({ projectSlug }) => [
  { key: "displayIdentity", label: "Participant" },
  { key: "currentStage", label: "Current stage", getValue: row => currentStageLabel(row.progress) },
  { key: "practice", label: "Practice", getValue: row => participantStatusValue(row.evidence, "completed-sessions") },
  { key: "reflection", label: "Reflection", getValue: row => reflectionValue(row.evidence) },
  { key: "finalRating", label: "Final rating", getValue: row => finalRatingValue(row.evidence, row.progress) },
  { key: "lastActivityAt", label: "Recent activity", getValue: row => row.lastActivityAt ? new Date(row.lastActivityAt).toLocaleDateString() : "—" },
  { key: "action", label: "", sortable: false, getValue: row => <Link className="av-project-facilitator-members__action" to={`/app/facilitate/project/${projectSlug}/member/${row.membershipId}`}>View participant</Link> },
];
